/**
 * Core: Cart API
 * -----------------------------------------------------------------------------
 * Public cart interface for third-party integrations.
 * - Registered before Vue mounts, so calls from asynchronously loaded vendor
 *   scripts are queued rather than thrown away.
 *
 */
import { CART_API_TIMEOUT } from '~/config/const'

/**
 * Set global variables.
 */
const queue = []
let methods = false
let timeout = false

/**
 * Build a cart API error.
 * @param {String} code - Stable error code for integrations to branch on.
 * @param {String} message - Human readable message.
 * @param {String} [detail] - Underlying error description, if any.
 * @returns {Object}
 */
export function cartApiError(code, message, detail = '') {
  return { code, detail, message }
}

/**
 * Reject everything queued, the theme never finished starting.
 */
function rejectQueue() {
  while (queue.length) {
    queue.shift().reject(
      cartApiError('THEME_NOT_READY', 'The cart is unavailable on this page'),
    )
  }
}

/**
 * Register the implementation and flush anything that arrived early.
 * @param {Object} implementation - Object of methods keyed by API method name.
 */
export function registerCartApi(implementation) {
  methods = implementation
  clearTimeout(timeout)

  while (queue.length) {
    const { name, payload, reject, resolve } = queue.shift()
    methods[name](payload).then(resolve, reject)
  }
}

/**
 * Call an API method, queueing it until the implementation is registered.
 * @param {String} name - Method name.
 * @param {*} payload - Method payload.
 * @returns {Promise}
 */
function call(name, payload) {
  if (methods) {
    return methods[name](payload)
  }

  return new Promise((resolve, reject) => {
    queue.push({ name, payload, reject, resolve })

    clearTimeout(timeout)
    timeout = setTimeout(rejectQueue, CART_API_TIMEOUT)
  })
}

/**
 * Create a new cart API object.
 */
export default () => {
  /**
   * Expose public interface.
   */
  return Object.freeze({
    AddToCart: (payload) => call('addToCart', payload),
    GetUpsells: (payload) => call('getUpsells', payload),
  })
}
